import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';

import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import FileInput from '@amzn/meridian/file-input';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';

import { FileRow, FileStatus } from 'src/components/Agreements/FileUploader/FileRow';
import { LandingZone } from 'src/components/Agreements/FileUploader/LandingZone';

interface IFileUploader extends PropsWithChildren {}

interface FileItem {
    id: number;
    file: File;
}

const AUTO_CLOSE_DELAY_SECONDS = 3;

export const FileUploader: React.FC<IFileUploader> = (props) => {
    const [files, setFiles] = useState<FileItem[]>([]);
    const [startUpload, setStartUpload] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [nameWidth, setNameWidth] = useState(75);
    const [fileStatus, setFileStatus] = useState(new Map<number, FileStatus>());
    const numReady = useMemo(() => {
        return Array.from(fileStatus.values()).filter((status) => status === 'ready').length;
    }, [fileStatus]);
    const [showSuccessBanner, setShowSuccessBanner] = useState(false);

    const onFileAttached = useCallback((files: File[]) => {
        setFiles(files.map((file, idx) => ({ id: idx, file })));
        setNameWidth(
            Math.min(
                115,
                files.map((file) => file.name.length / 2 + 2).reduce((a, b) => Math.max(a, b), 15)
            )
        );
    }, []);

    useEffect(() => {
        function handler(e: DragEvent) {
            setIsDragging(!isModalOpen);
        }

        window.addEventListener('dragenter', handler);

        return () => window.removeEventListener('dragenter', handler);
    }, [isModalOpen]);

    const onCancel = useCallback(() => {
        setFiles([]);
        setStartUpload(false);
        setIsModalOpen(false);
        setShowSuccessBanner(false);
    }, []);

    useEffect(() => {
        const numDone = Array.from(fileStatus.values()).filter((status) => status === 'done').length;

        if (isModalOpen && numDone === fileStatus.size) {
            if (fileStatus.size > 0) {
                setShowSuccessBanner(true);
                setTimeout(onCancel, AUTO_CLOSE_DELAY_SECONDS * 1000);
            }
        }
    }, [fileStatus, onCancel, isModalOpen]);

    useEffect(() => {
        if (isModalOpen && files.length === 0) {
            onCancel();
        } else if (!isModalOpen && files.length > 0) {
            setIsModalOpen(true);
        }
    }, [files, onCancel, isModalOpen]);

    useEffect(() => {
        const ids = new Set(files.map((file) => file.id));

        setFileStatus((prev) => {
            return new Map(Array.from(prev.entries()).filter(([id, _]) => ids.has(id)));
        });
    }, [files]);

    const removeFile = useCallback((index: number) => {
        setFiles((prev) => {
            const before = prev.slice(0, index);
            const after = prev.slice(index + 1);
            return [...before, ...after];
        });
    }, []);

    return (
        <>
            <FileInput
                type={'multiple'}
                showDropZone={false}
                accept={'.pdf'}
                uploadButtonSize={'small'}
                uploadButtonType={'tertiary'}
                onFileAttached={(files) => onFileAttached(files)}
            />
            <LandingZone
                hidden={!isDragging}
                onDragLeave={() => setIsDragging(false)}
                onFileAttached={onFileAttached}
            />
            <Modal title={'Upload Documents'} open={isModalOpen} scrollContainer={'modal'}>
                <Column>
                    {showSuccessBanner && (
                        <Alert type={'success'} size={'medium'}>
                            {`All uploads succeeded. Auto-closing in ${AUTO_CLOSE_DELAY_SECONDS} seconds.`}
                        </Alert>
                    )}
                    <Table headerRows={1} rowComponents={[TableRow, FileRow]}>
                        <TableRow>
                            <TableCell>Status</TableCell>
                            <TableCell>File name</TableCell>
                            <TableCell>Size</TableCell>
                            <TableCell>Contract Central</TableCell>
                            <TableCell>Approvals</TableCell>
                        </TableRow>
                        {files.map((item, idx) => (
                            <FileRow
                                id={item.id}
                                file={item.file}
                                key={item.id}
                                width={nameWidth}
                                startUpload={startUpload}
                                setReady={setFileStatus}
                                onRemove={() => removeFile(idx)}
                            />
                        ))}
                    </Table>
                </Column>
                <ModalFooter>
                    <Row alignmentHorizontal={'end'}>
                        <Button onClick={onCancel} type={'tertiary'}>
                            Cancel
                        </Button>
                        <Button onClick={() => setStartUpload(true)} disabled={numReady < 1}>
                            {`Upload ${numReady} file${numReady !== 1 ? 's' : ''}`}
                        </Button>
                    </Row>
                </ModalFooter>
            </Modal>
        </>
    );
};
